import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { kindOf, eventEmoji } from "@/lib/events";

/**
 * UpcomingHighlightBanner (legacy export name kept) — gentle nudge
 * that fires 30 days out from a family member's birthday so adults
 * can start planning without it sneaking up. If there's no birthday
 * inside 30 days, falls back to spotlighting one major non-recurring
 * event inside the next 14 days (a wedding, day trip, weekend away,
 * a friend's birthday on the calendar, etc.).
 *
 * Birthdays use a 30-day window because they need cake/gift planning
 * lead time. Other events use 14 days because most parties / trips
 * already have their own logistics — the banner is just a friendly
 * "this is the big one this fortnight" reminder.
 *
 * One banner at a time. Chaining would feel like a notification jail.
 * Dismissable per-target-per-device via localStorage.
 */
export default function BirthdayPlanningBanner({ members = [], events = [] }) {
  const [dismissedKey, setDismissedKey] = useState(() => {
    try {
      return localStorage.getItem("ff_bday_banner_dismiss") || "";
    } catch {
      return "";
    }
  });
  // 1-day "no banners" cooldown after any dismissal so the family
  // doesn't get a fresh major-event spotlight the moment they swat the
  // birthday one away (or vice-versa). Stored as an ISO timestamp so
  // it survives page reloads and is timezone-resilient.
  const [cooldownUntil, setCooldownUntil] = useState(() => {
    try {
      return localStorage.getItem("ff_bday_banner_cooldown_until") || "";
    } catch {
      return "";
    }
  });
  const inCooldown = useMemo(() => {
    if (!cooldownUntil) return false;
    const t = new Date(cooldownUntil).getTime();
    return Number.isFinite(t) && t > Date.now();
  }, [cooldownUntil]);

  // 1) Closest upcoming family birthday in 3–30 days. Single closest wins.
  const upcomingBirthday = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let best = null;
    for (const m of members) {
      if (!m.birthday) continue;
      // birthday is "YYYY-MM-DD" or "MM-DD" — both supported
      const parts = m.birthday.split("-");
      if (parts.length < 2) continue;
      const bm = parseInt(parts[parts.length - 2], 10) - 1;
      const bd = parseInt(parts[parts.length - 1], 10);
      if (isNaN(bm) || isNaN(bd)) continue;
      let next = new Date(today.getFullYear(), bm, bd);
      next.setHours(0, 0, 0, 0);
      if (next < today) {
        next = new Date(today.getFullYear() + 1, bm, bd);
      }
      const days = Math.round((next - today) / (1000 * 60 * 60 * 24));
      if (days < 3 || days > 30) continue;
      if (!best || days < best.days) {
        best = {
          kind: "birthday",
          member: m,
          days,
          dateKey: `${next.getFullYear()}-${String(bm + 1).padStart(2, "0")}-${String(bd).padStart(2, "0")}`,
        };
      }
    }
    return best;
  }, [members]);

  // 2) If no family birthday in window, look for one "major" event in
  //    the next 14 days. We rank candidates by:
  //    a) emoji-on-calendar birthdays (manually-added "Jonas's birthday")
  //       — these are non-family birthdays the parents added directly
  //    b) multi-day events / trips (travel, day_trip)
  //    c) special-day kinds: school_event, event
  //    Tiebreaker: nearest in time wins.
  const upcomingMajor = useMemo(() => {
    if (upcomingBirthday) return null; // birthday wins outright
    const now = new Date();
    const horizon = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
    let best = null;
    for (const ev of events || []) {
      if (!ev || !ev.starts_at || ev.id?.startsWith("bday-")) continue;
      // Skip our own family-birthday synthesised events — already
      // covered by upcomingBirthday above. Real birthdays added to the
      // calendar by hand have kind="birthday" and a regular id.
      if (ev.source === "birthday") continue;
      const start = new Date(ev.starts_at);
      if (isNaN(start.getTime())) continue;
      if (start < now || start > horizon) continue;
      // Score the event so a wedding/trip beats a 1-hour activity.
      let score = 0;
      const kindKey = ev.kind || "other";
      if (kindKey === "birthday") score += 10; // hand-added friend's birthday
      if (kindKey === "travel" || kindKey === "day_trip") score += 8;
      if (kindKey === "event") score += 6;
      if (kindKey === "school_event") score += 5;
      if (ev.ends_at) {
        const end = new Date(ev.ends_at);
        const hours = (end - start) / (1000 * 60 * 60);
        if (hours >= 24) score += 4; // multi-day
        else if (hours >= 4) score += 2; // half-day plus
      }
      if (score < 4) continue; // ignore short, low-stakes things
      const days = Math.max(0, Math.round((start - now) / (1000 * 60 * 60 * 24)));
      const ranked = { kind: "event", event: ev, days, score };
      if (!best || best.score < score || (best.score === score && days < best.days)) {
        best = ranked;
      }
    }
    if (!best) return null;
    return {
      kind: "event",
      event: best.event,
      days: best.days,
      dateKey: best.event.starts_at.slice(0, 10),
    };
  }, [events, upcomingBirthday]);

  const banner = upcomingBirthday || upcomingMajor;
  // Recompute the dismiss key whenever the spotlit target changes so a
  // dismissed birthday doesn't suppress a later major-event spotlight.
  const dismissKey = useMemo(() => {
    if (!banner) return "";
    if (banner.kind === "birthday") return `b:${banner.member.id}:${banner.dateKey}`;
    return `e:${banner.event.id}:${banner.dateKey}`;
  }, [banner]);

  // Reset state when the relevant key changes — important because the
  // localStorage value persists across reloads but the banner moves on.
  useEffect(() => {
    if (!banner) return;
    try {
      const stored = localStorage.getItem("ff_bday_banner_dismiss") || "";
      if (stored !== dismissedKey) setDismissedKey(stored);
    } catch {}
  }, [banner, dismissKey]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!banner) return null;
  if (inCooldown) return null;
  if (dismissedKey === dismissKey) return null;

  const dismiss = () => {
    const until = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    try {
      localStorage.setItem("ff_bday_banner_dismiss", dismissKey);
      localStorage.setItem("ff_bday_banner_cooldown_until", until);
    } catch {}
    setDismissedKey(dismissKey);
    setCooldownUntil(until);
  };

  // Format the banner copy + emoji + tint based on which case we hit.
  let emoji = "🎂";
  let topLabel = "";
  let title = "";
  let tint = "var(--bg-soft)";
  let testid = "upcoming-highlight-banner";

  if (banner.kind === "birthday") {
    emoji = "🎂";
    topLabel = `In ${banner.days} days`;
    title = `Time to plan ${banner.member.name}'s birthday.`;
    testid = `birthday-planning-banner-${banner.member.id}`;
  } else {
    const ev = banner.event;
    emoji = eventEmoji(ev) || "📅";
    topLabel = banner.days === 0
      ? "Today"
      : banner.days === 1
        ? "Tomorrow"
        : `In ${banner.days} days`;
    // "Time to get ready for Sus and Chris's wedding in 14 days."
    const niceDays = banner.days <= 1 ? "soon" : `in ${banner.days} days`;
    title = `Time to get ready for ${ev.title} ${niceDays}.`;
    // Soft kind tint so different upcoming things feel different.
    const k = kindOf(ev.kind);
    if (k && k.tint) tint = `color-mix(in srgb, var(--bg) 70%, ${k.tint})`;
    testid = `upcoming-highlight-banner-${ev.id}`;
  }

  return (
    <div
      className="ff-card p-3 flex items-center gap-3 mb-3"
      style={{ background: tint }}
      data-testid={testid}
    >
      <span className="text-2xl leading-none flex-shrink-0" aria-hidden="true">
        {emoji}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[12px] uppercase tracking-[0.06em] text-[color:var(--ink-3)]">
          {topLabel}
        </div>
        <div className="text-[15px] font-display leading-tight tracking-tight truncate">
          {title}
        </div>
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="w-7 h-7 rounded-full hover:bg-[color:var(--bg)] flex items-center justify-center text-[color:var(--ink-3)] flex-shrink-0"
        aria-label="Dismiss"
        data-testid="birthday-planning-banner-dismiss"
      >
        <X className="w-4 h-4" strokeWidth={2} />
      </button>
    </div>
  );
}
